import { Link } from "react-router-dom";
import { Phone, MessageSquare, Bot, Check, ShieldCheck, Star } from "lucide-react";
import { PHONE, TEL, SMS } from "@/lib/config";
import { openGhost } from "./GhostAI";
import { useContent } from "@/lib/ContentContext";

export const Overline = ({ children, className = "" }) => (
  <span className={`overline text-[var(--action)] text-sm ${className}`}>{children}</span>
);

export const SectionHeading = ({ overline, title, subtitle, center, light }) => (
  <div className={`${center ? "text-center mx-auto max-w-2xl" : "max-w-2xl"} mb-10`}>
    {overline && <Overline>{overline}</Overline>}
    <h2 className={`font-head font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-2 ${light ? "text-white" : "text-[var(--navy)]"}`}>{title}</h2>
    {subtitle && <p className={`mt-3 text-base leading-relaxed ${light ? "text-white/70" : "text-[var(--text-muted)]"}`}>{subtitle}</p>}
  </div>
);

export const PrimaryBtn = ({ to, href, children, className = "", ...rest }) => {
  const cls = `inline-flex items-center justify-center gap-2 btn-lift bg-[var(--red)] hover:bg-[var(--red-dark)] text-white font-semibold px-6 py-3 rounded-full ${className}`;
  if (to) return <Link to={to} className={cls} {...rest}>{children}</Link>;
  return <a href={href} className={cls} {...rest}>{children}</a>;
};

export const OutlineBtn = ({ to, href, onClick, children, className = "", ...rest }) => {
  const cls = `inline-flex items-center justify-center gap-2 btn-lift bg-white border-2 border-[var(--navy)]/15 hover:border-[var(--action)] text-[var(--navy)] font-semibold px-6 py-3 rounded-full ${className}`;
  if (to) return <Link to={to} className={cls} {...rest}>{children}</Link>;
  if (href) return <a href={href} className={cls} {...rest}>{children}</a>;
  return <button onClick={onClick} className={cls} {...rest}>{children}</button>;
};

export const CTAGroup = ({ ghost = true }) => (
  <div className="flex flex-wrap gap-3">
    <PrimaryBtn to="/get-a-quote" data-testid="cta-quote">Get a Free Quote</PrimaryBtn>
    <OutlineBtn href={TEL} data-testid="cta-call"><Phone className="w-4 h-4 text-[var(--red)]" /> Call {PHONE}</OutlineBtn>
    <OutlineBtn href={SMS} data-testid="cta-text"><MessageSquare className="w-4 h-4 text-[var(--action)]" /> Text Us</OutlineBtn>
    {ghost && <OutlineBtn onClick={openGhost} data-testid="cta-ghost"><Bot className="w-4 h-4" /> Ask Ghost AI</OutlineBtn>}
  </div>
);

const TRUST = ["Veteran-Owned", "Licensed & Insured", "Residential & Commercial", "Free Quote Requests", "Local Orlando Service"];
export const TrustStrip = ({ light }) => (
  <div className="flex flex-wrap gap-x-5 gap-y-2" data-testid="trust-indicators">
    {TRUST.map((t) => (
      <span key={t} className={`flex items-center gap-1.5 text-sm font-medium ${light ? "text-white/90" : "text-[var(--navy)]"}`}>
        <Check className="w-4 h-4 text-[var(--aqua)]" /> {t}
      </span>
    ))}
  </div>
);

export const FinancingNote = ({ className = "" }) => {
  const { content } = useContent();
  return (
    <div className={`flex items-start gap-2.5 text-sm bg-[var(--surface)] border border-[var(--action)]/20 rounded-xl px-4 py-3 ${className}`} data-testid="financing-note">
      <ShieldCheck className="w-5 h-5 text-[var(--action)] shrink-0 mt-0.5" />
      <p className="text-[var(--text-muted)]">
        {content.financing_note || "Financing options may be available through EasyQuickFund."}{" "}
        <a href={content.financing_url || "https://www.easyquickfund.com"} target="_blank" rel="noopener noreferrer"
           className="text-[var(--action)] font-semibold underline">Explore Financing Options</a>
      </p>
    </div>
  );
};

export const Stars = ({ n = 5 }) => (
  <div className="flex gap-0.5">{Array.from({ length: n }).map((_, i) => <Star key={i} className="w-4 h-4 fill-[#F6B21B] text-[#F6B21B]" />)}</div>
);

export const PageHeader = ({ overline, title, subtitle }) => (
  <section className="bg-[var(--navy)] text-white pt-14 pb-16" data-testid="page-header">
    <div className="max-w-7xl mx-auto px-4">
      {overline && <Overline className="text-[var(--aqua)]">{overline}</Overline>}
      <h1 className="font-head font-extrabold uppercase text-4xl sm:text-5xl tracking-tight mt-2">{title}</h1>
      {subtitle && <p className="text-white/75 mt-3 max-w-2xl leading-relaxed">{subtitle}</p>}
    </div>
  </section>
);
